import type { UnwrapNestedRefs } from "vue";
import {
  type SkMaterial,
  Quote_Status_Dataset_Enum,
  QuoteLineItemFieldsFragment as QuoteLineItemType,
  SkAddress,
  SkContract,
  SkCustomer,
  SkNwa,
  SkPlant,
  SkPriceList,
  SkWell,
  SkWellAddress,
  UsersFieldsFragment,
} from "gql/api";

interface Ref<T> {
  value: T;
};

export type CanBeMissing<T> = T | null | undefined;

export type Material = {
  id: string;
  active: boolean;
  api_response_count: number;
};

export type MaterialQueryParams = {
  country?: string;
  deleted: string,
  exclude_trade_in: string;
  expand: string;
  ordering: string;
  number__in?: string[];
  price_list?: string;
  pricing_date?: string;
  in_stock?: string;
  cable_in_stock?: string;
  owner?: string | null;
  number?: string;
  contract?: string;
  has_pricing?: boolean;
  search?: string;
  advanced_search?: string;
  excludes?: string;
  material_type__in?: string;
  page?: number;
  page_size?: number;
  plant?: string;
  prod_hierarchies?: string;
  prod_hierarchy?: string;
};

// TODO: typings for nested objects
export type QuotesSearch = {
  quoteSearch: string;
  nwaSearch: any;
  skProposalNumber: string | null;
  userSearch: any;
  priceListSearch: any;
  contractSearch: any | null;
  salesDistrictSearch: any;
  wellAddressSearch: any;
  pricingDate: Date | null;
  proposedStartDate: Date | null;
  currency: any;
  versionNumber: number;
  scenarioSearch: null | undefined | any;
  skJobType: null | string;
  sk_job_number: string | null | undefined;  // conditional field
  sk_job_id: string | number | null | undefined; // conditional field
  skPlanningOrder: null | string;
  status: string;
  repName: string;
  repEmail: string;
  repPhone: string;
  note: string;
  comments: string;
  directions: string;
};

export type QuotesListAdvancedSearch = {
  quoteSearch: string;
  proposalSearch: string;
  nwaSearch: string | SkNwa;
  userSearch: string | UsersFieldsFragment;
  priceListSearch: string | SkPriceList;
  contractSearch: string | SkContract;
  salesDistrictSearch: string | SkPlant;
  wellSearch: string | SkWellAddress;
  addressSearch: string | SkWellAddress;
  wellAddressSearch: string | SkWellAddress;
  customerSearch: string | SkCustomer;
  statusSearch: string | StatusItem;
  pricingDate: Date | null;
};

export type NwaSearchParams = {
  active: string;
  canada_only_nwa?: string;
  deleted: string;
  id?: string;
  name__icontains?: string;
  ordering: string;
  expand: string;
};

export type PlantSearchParams = {
  deleted: string;
  group_not_in: string;
  is_active: string;
  page_size: string;
  search: string;
};

export type ContractSearchParams = {
  deleted: string;
  active: string;
  expand: string;
  ordering: string;
  page_size?: string;
  number__icontains: string; // <--- TODO: seems to have no effect on backend results
  search?: string; // Optional search method. You probably want number__icontains.
  customer?: string;
};

export type CustomerSearchParams = {
  deleted: string;
  search: string; // the search string
}

export type WellAddressSearchParams = {
  // Pick one of the 3 following search params:
  // - if you need a general SkWellAddress search, use search
  // - if you want to gaurantee specifics of well, use well_search
  // - if you want to gaurantee specifics of address, use address_search
  search?: string; 
  address_search?: string;
  well_search?: string;
  expand: string;
  inventory_source?: string;
  deleted?: string;
  ordering?: string;
  address_id?: string;
  customer_name?: string;
  well_id?: string;
  uwi?: string;
  include_null_addresses?: string;
};

export type PriceListSearchParams = {
  active: string;
  deleted: string;
  ordering: string;
  name__icontains: string;
};

export type CurrencySearchParams = {
  page_size: string;
  active: string;
  deleted: string;
  code__icontains: string
}

export type JobSearchParams = {
  sk_job_id: number | null | undefined;
  deleted: string
}

export type PloSearchParams = {
  customer_id: string;
  well_id?: string;
  address_id?: string;
}

export type NWAItem = {
  // id: string;
  // name: string;
  // active: boolean;
  // deleted: boolean;
  // deprecated: boolean;
  // country: string;
  value: string;
  label: string;
};

export type StatusItem = {
  value: Quote_Status_Dataset_Enum;
  label: string;
};

export type MaterialTypeSelection =
  | "all"
  | "sale_finished"
  | "small_parts"
  | "customer_owned"
  | "rental"
  | "trade_in";

export type SidePanelFilters = {
  materialSearchInput: string;
  excludeInput: string;
  productHierarchyInput: string;
  basicSearch: boolean;
  inStockFilter: boolean;
  pricedFilter: boolean;
  cableFilter: boolean;
  // Single, mutually-exclusive material-type selection (replaces the former
  // finishedGoods / coFilter / rentalFilter / tradeinFilter checkboxes).
  materialTypeSelection: MaterialTypeSelection;
  standardOfferingFilter: boolean;
  allPlantsFilter: boolean;
  page: number;
  pageSize: number;
};

export type UIMaterial = SkMaterial & {
  line_item_type?: string;
  category?: string;
  custom_price: number;
  quantity: number;
  is_custom: boolean;
  number: string;
  from_contract?: boolean;
  __typename: string;
};

export type GenericPropType = Record<string, any> | null;

// Match order of quote line item categories
export const spyglassTypes = [
  "pump",
  "intake",
  "seal",
  "motor",
  "sensor",
  "cable",
  "motor_lead_extension",
  "variable_speed_drive",
  "step_up_transformer",
  "step_down_transformer",
  "switchboard",
] as const satisfies (keyof QuoteLineItemType)[];

// number here means "give me the type of any element in the array"
export type SpyglassType = typeof spyglassTypes[number];

export type GqlQli = {
  _set: { deleted_at: string },
  where: { id: {_eq: string} }
}
